import { ChartInteractiveLayer } from "app/charts/ChartInteractiveLayer/ChartInteractiveLayer";
import { calcValues } from "app/charts/functions";
import { numberFormattingContext } from "app/formatting/context";
import { formatMoney } from "app/formatting/functions";
import React from "react";

import "./style.scss";
import { chartPeriods } from "app/charts/types";
import { TimeAxis, TimeAxisProps } from "app/charts/TimeAxis/TimeAxis";
import { NoData, NoDataIcon } from "app/errors/NoData/NoData";
import { CHART_CANVAS_WIDTH } from "app/charts/constants";

/**
 * [timestamp, value]
 */
export type ChartData = [number, number][];

export interface Props {
    value: ChartData;
    error?: any;
    className?: string;
    period: chartPeriods;
    todayExchangeOpen?: string | null;
    lastExchangeClose?: string | null;
    lastExchangeCloseValue?: number;
    chartWidth?: number;
    timeAxisComponent?: React.FC<TimeAxisProps>;
}

export const BasicChart = ({
    className,
    value,
    error,
    period,
    todayExchangeOpen,
    lastExchangeClose,
    lastExchangeCloseValue,
    chartWidth = CHART_CANVAS_WIDTH,
    timeAxisComponent = TimeAxis
}: Props) => {
    const [renderInteractiveLayer, setRenderInteractiveLayer] = React.useState(false);
    const fractionDigits = React.useContext(numberFormattingContext).fractionDigits;
    const classNames = ["basicChart"];
    const Axis = timeAxisComponent;

    React.useEffect(() => {
        setRenderInteractiveLayer(true);
    }, [setRenderInteractiveLayer]);

    // Calculate the path and support information, but only when data changes.
    const [
        pathData,
        min,
        max,
        avg,
        maxAvg,
        avgMin,
        fenceStart,
        fenceEnd,
        lastExchangeClosePercent,
        isRising,
        getDataFunction
    ] = React.useMemo(
        () => calcValues(value, period, chartWidth, todayExchangeOpen, lastExchangeClose, lastExchangeCloseValue),
        [value, period, chartWidth, todayExchangeOpen, lastExchangeClose,lastExchangeCloseValue]
    );

    if (isRising) {
        classNames.push("rising");
    }

    if (error) {
        classNames.push("empty");
    }

    if (className) {
        classNames.push(className);
    }

    return (
        <div className={classNames.join(" ")}>
            <div className="wrap">
                <svg preserveAspectRatio="none" viewBox={`0 0 ${chartWidth} 100`} xmlns="http://www.w3.org/2000/svg">
                    <path d={pathData} />
                </svg>
                <dl>
                    <div className="max">
                        <dt>Hoch</dt>
                        <dd>{max != null ? formatMoney(max, "", fractionDigits) : ""}</dd>
                    </div>
                    <div className="maxmid">
                        <dt>Zwischenwert Hoch / Mittel</dt>
                        <dd>{maxAvg != null ? formatMoney(maxAvg, "", fractionDigits) : ""}</dd>
                    </div>
                    <div className="mid">
                        <dt>Mittel</dt>
                        <dd>{avg != null ? formatMoney(avg, "", fractionDigits) : ""}</dd>
                    </div>
                    <div className="midmin">
                        <dt>Zwischenwert Mittel / Tief</dt>
                        <dd>{avgMin != null ? formatMoney(avgMin, "", fractionDigits) : ""}</dd>
                    </div>
                    <div className="min">
                        <dt>Tief</dt>
                        <dd>{min != null ? formatMoney(min, "", fractionDigits) : ""}</dd>
                    </div>
                    <CloseLine
                        period={period}
                        fractionDigits={fractionDigits}
                        lastExchangeCloseValue={lastExchangeCloseValue}
                        lastExchangeClosePercent={lastExchangeClosePercent}
                    />
                </dl>
                {renderInteractiveLayer && getDataFunction && (
                    <ChartInteractiveLayer period={period} getData={getDataFunction} />
                )}
            </div>
            {fenceStart && fenceEnd && (
                <Axis dataPoints={value} fenceStart={fenceStart} fenceEnd={fenceEnd} period={period} />
            )}
            {error && (
                <NoData
                    icon={NoDataIcon.CrossedOut}
                    label="Aktuell haben wir keine Daten für diesen Zeitraum verfügbar"
                />
            )}
        </div>
    );
};

interface CloseLineProps {
    period: chartPeriods;
    fractionDigits: number;
    lastExchangeCloseValue?: number;
    lastExchangeClosePercent?: number | null;
}

const CloseLine = ({ period, fractionDigits, lastExchangeCloseValue, lastExchangeClosePercent }: CloseLineProps) => {
    if (!lastExchangeCloseValue || lastExchangeClosePercent === null || period !== chartPeriods.INTRADAY) {
        return null;
    }

    return (
        <div className="close" style={{ bottom: `${lastExchangeClosePercent}%` }}>
            <dt>
                Vortagesschluss
            </dt>
            <dd>{lastExchangeCloseValue ? formatMoney(lastExchangeCloseValue, "", fractionDigits) : ""}</dd>
        </div>
    );
};
